import { useState, useEffect, useCallback } from 'react';
import { AdminPageShell } from '../components/AdminShared';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { FileText, Plus, Edit, Trash2, Eye, Search, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function api(path, opts = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...opts.headers } })
    .then(async r => { if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || r.statusText); } return r.json(); });
}

const CLUSTERS = [
  { value: 'service-explainers', label: 'Service Explainers' },
  { value: 'industry-specific', label: 'Industry Insights' },
  { value: 'immigration-uscis', label: 'Immigration & USCIS' },
  { value: 'compliance', label: 'Language Access & Compliance' },
  { value: 'buyer-guides', label: 'Buyer Guides & Comparisons' },
  { value: 'use-cases', label: 'Use Cases & How-To' },
];

const selectCls = "w-full mt-1 h-10 px-3 border border-border rounded-md text-sm bg-background text-foreground";
const textareaCls = "w-full mt-1 px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground resize-y font-mono";

export default function AdminBlogPage() {
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [dialog, setDialog] = useState(null); // 'create' | 'edit'
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', slug: '', excerpt: '', content: '', meta_title: '', meta_description: '', cluster: '', tags: '', author: '', status: 'draft', featured_image: '', faq: '' });

  const fetchPosts = useCallback(() => {
    const params = new URLSearchParams({ page, limit: 20 });
    if (filter) params.set('status', filter);
    api(`/api/admin/blog/posts?${params}`).then(d => { setPosts(d.posts || []); setTotal(d.total || 0); }).catch(() => {});
  }, [page, filter]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const openCreate = () => {
    setForm({ title: '', slug: '', excerpt: '', content: '', meta_title: '', meta_description: '', cluster: '', tags: '', author: 'Interpreter Services', status: 'draft', featured_image: '', faq: '' });
    setSelected(null);
    setDialog('create');
  };

  const openEdit = async (post) => {
    try {
      const full = await api(`/api/admin/blog/posts/${post.slug}`);
      const faqStr = full.faq && full.faq.length > 0 ? JSON.stringify(full.faq, null, 2) : '';
      setForm({ ...full, tags: (full.tags || []).join(', '), faq: faqStr });
      setSelected(full);
      setDialog('edit');
    } catch (e) { toast.error(e.message); }
  };

  const save = async () => {
    setSaving(true);
    try {
      let faq = [];
      if (form.faq.trim()) {
        try { faq = JSON.parse(form.faq); } catch { toast.error('FAQ must be valid JSON array of {q, a} objects'); setSaving(false); return; }
      }
      const payload = { ...form, tags: form.tags ? form.tags.split(',').map(s => s.trim()).filter(Boolean) : [], faq };
      if (dialog === 'create') {
        await api('/api/admin/blog/posts', { method: 'POST', body: JSON.stringify(payload) });
        toast.success('Post created');
      } else {
        await api(`/api/admin/blog/posts/${selected.slug}`, { method: 'PUT', body: JSON.stringify(payload) });
        toast.success('Post updated');
      }
      setDialog(null);
      fetchPosts();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const del = async (post) => {
    if (!window.confirm(`Delete "${post.title}"?`)) return;
    try { await api(`/api/admin/blog/posts/${post.slug}`, { method: 'DELETE' }); toast.success('Post deleted'); fetchPosts(); } catch (e) { toast.error(e.message); }
  };

  return (
    <AdminPageShell title="Blog Management" subtitle="Create and manage blog articles" testId="admin-blog">
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-2">
          {['', 'published', 'draft'].map(s => (
            <button key={s} onClick={() => { setFilter(s); setPage(1); }} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${filter === s ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`} data-testid={`blog-filter-${s || 'all'}`}>
              {s === '' ? 'All' : s === 'published' ? 'Published' : 'Drafts'}
            </button>
          ))}
        </div>
        <Button onClick={openCreate} data-testid="blog-create-btn"><Plus className="w-4 h-4 mr-1" />New Post</Button>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="text-left px-4 py-2 font-medium text-muted-foreground">Title</th>
              <th className="text-left px-4 py-2 font-medium text-muted-foreground">Cluster</th>
              <th className="text-left px-4 py-2 font-medium text-muted-foreground">Status</th>
              <th className="text-left px-4 py-2 font-medium text-muted-foreground">Views</th>
              <th className="text-left px-4 py-2 font-medium text-muted-foreground">Date</th>
              <th className="text-right px-4 py-2 font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {posts.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No posts yet. Click "New Post" to create your first article.</td></tr>
            ) : posts.map(p => (
              <tr key={p.slug} className="hover:bg-muted/50">
                <td className="px-4 py-2">
                  <p className="font-medium text-foreground truncate max-w-xs">{p.title}</p>
                  <p className="text-xs text-muted-foreground">/blog/{p.slug}</p>
                </td>
                <td className="px-4 py-2 text-xs text-muted-foreground">{CLUSTERS.find(c => c.value === p.cluster)?.label || p.cluster || '-'}</td>
                <td className="px-4 py-2">
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${p.status === 'published' ? 'bg-green-900/20 text-green-400' : 'bg-muted text-muted-foreground'}`}>{p.status}</span>
                </td>
                <td className="px-4 py-2 text-muted-foreground">{p.views || 0}</td>
                <td className="px-4 py-2 text-xs text-muted-foreground">{p.published_at ? new Date(p.published_at).toLocaleDateString() : '-'}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  {p.status === 'published' && <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer"><Button variant="ghost" size="sm"><Eye className="w-4 h-4" /></Button></a>}
                  <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`blog-edit-${p.slug}`}><Edit className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(p)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground mt-2">{total} post{total !== 1 ? 's' : ''}</p>

      {/* Create/Edit Dialog */}
      <Dialog open={!!dialog} onOpenChange={o => { if (!o) setDialog(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{dialog === 'create' ? 'New Blog Post' : 'Edit Post'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><Label>Title *</Label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="mt-1" data-testid="blog-title-input" /></div>
              <div><Label>Slug</Label><Input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} className="mt-1" placeholder="auto-generated from title" /></div>
              <div><Label>Status</Label>
                <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className={selectCls}>
                  <option value="draft">Draft</option><option value="published">Published</option>
                </select></div>
            </div>
            <div><Label>Excerpt</Label><textarea value={form.excerpt} onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))} rows={2} className={textareaCls} placeholder="Brief summary for listings and social sharing..." /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Cluster</Label>
                <select value={form.cluster} onChange={e => setForm(f => ({ ...f, cluster: e.target.value }))} className={selectCls}>
                  <option value="">Select cluster</option>{CLUSTERS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select></div>
              <div><Label>Tags (comma-separated)</Label><Input value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} className="mt-1" placeholder="interpreting, healthcare, USCIS" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Meta Title</Label><Input value={form.meta_title} onChange={e => setForm(f => ({ ...f, meta_title: e.target.value }))} className="mt-1" placeholder="SEO title override" /></div>
              <div><Label>Author</Label><Input value={form.author} onChange={e => setForm(f => ({ ...f, author: e.target.value }))} className="mt-1" /></div>
            </div>
            <div><Label>Meta Description</Label><textarea value={form.meta_description} onChange={e => setForm(f => ({ ...f, meta_description: e.target.value }))} rows={2} className={textareaCls} placeholder="SEO meta description (155 chars ideal)" /></div>
            <div><Label>Featured Image URL</Label><Input value={form.featured_image} onChange={e => setForm(f => ({ ...f, featured_image: e.target.value }))} className="mt-1" placeholder="https://..." /></div>
            <div><Label>Content (Markdown)</Label><textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} rows={18} className={textareaCls} placeholder="## Section heading&#10;&#10;Paragraph text with **bold** and [links](url)..." /></div>
            <div><Label>FAQ (JSON)</Label><textarea value={form.faq} onChange={e => setForm(f => ({ ...f, faq: e.target.value }))} rows={4} className={textareaCls} placeholder='[{"q": "Question?", "a": "Answer."}]' /></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="blog-save-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                {dialog === 'create' ? 'Create Post' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminPageShell>
  );
}
